import { Link } from "react-router-dom";
import { ArrowRight, Clock, Calendar } from "lucide-react";
import SEO from "@/components/SEO";
import Logo from "@/components/Logo";
import { useLanguage } from "@/context/LanguageContext";
import { motion } from "framer-motion";
import { blogPosts } from "@/data/blog";
import { cities } from "@/data/cities";

const Blog = () => {
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Blog — Conseils web et digital pour PME"
        description="Guides, conseils et bonnes pratiques pour réussir votre présence en ligne. SEO, design, e-commerce et plus."
        path="/blog"
      />
      {/* Header */}
      <div className="bg-gradient-to-b from-primary/5 to-transparent pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-foreground hover:text-primary transition-colors mb-8 font-semibold"
          >
            <Logo className="w-6 h-6" />
            <span>
              {language === "fr" ? "Retour à l'accueil Lacrea" : language === "es" ? "Volver al inicio Lacrea" : "Back to Lacrea home"}
            </span>
          </Link>
          <h1
            className="text-4xl sm:text-5xl font-bold mb-4"
          >
            Blog
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            {language === "fr"
              ? "Conseils, guides et bonnes pratiques pour réussir votre présence en ligne."
              : language === "es"
              ? "Consejos, guías y buenas prácticas para tu presencia online."
              : "Tips, guides, and best practices for your online presence."}
          </p>
        </div>
      </div>

      {/* Articles */}
      <div className="max-w-4xl mx-auto px-6 pb-24">
        <div className="space-y-8">
          {blogPosts.map((post, index) => (
            <motion.article
              key={post.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={`/blog/${post.slug}`}
                className="group block bg-card rounded-2xl border border-border shadow-md hover:shadow-xl transition-shadow overflow-hidden"
              >
                <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-0">
                  {/* Image */}
                  <div className="aspect-[16/9] md:aspect-auto overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title[language] || post.title.fr}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-6 flex flex-col justify-center">
                    <div className="flex items-center gap-4 mb-3">
                      <span className="px-3 py-1 bg-primary/10 text-primary text-xs font-bold rounded-full">
                        {post.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        {new Date(post.date).toLocaleDateString(
                          language === "fr" ? "fr-FR" : language === "es" ? "es-ES" : "en-GB",
                          { day: "numeric", month: "long", year: "numeric" }
                        )}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {post.readTime}
                      </span>
                    </div>

                    <h2
                      className="text-xl font-bold mb-2 group-hover:text-primary transition-colors"
                    >
                      {post.title[language] || post.title.fr}
                    </h2>

                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                      {post.excerpt[language] || post.excerpt.fr}
                    </p>

                    <span className="inline-flex items-center gap-1 text-primary text-sm font-semibold group-hover:gap-2 transition-all">
                      {language === "fr" ? "Lire l'article" : language === "es" ? "Leer el artículo" : "Read article"}
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>

        {/* City pages internal links */}
        <div className="mt-16 pt-12 border-t border-border">
          <h2
            className="text-xl font-bold mb-6"
          >
            {language === "fr"
              ? "Création de site internet par ville"
              : language === "es"
              ? "Creación de sitios web por ciudad"
              : "Website creation by city"}
          </h2>
          <div className="flex flex-wrap gap-3">
            {cities.map((c) => (
              <Link
                key={c.slug}
                to={`/creation-site-internet/${c.slug}`}
                className="text-sm px-4 py-2 rounded-full bg-card border border-border hover:border-primary/30 hover:text-primary transition-colors"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;
